// hooks
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

/**
 * Keeps the newest `value` reachable from effects and callbacks without making it a reactive
 * dependency. Use it for values callers pass inline — callbacks, option objects — whose
 * identity changes every render but whose changes must not retrigger an effect.
 *
 * The ref is written from a layout effect, never during render: mutating a ref while
 * rendering is impure and stops the React Compiler from optimizing the hook, and a layout
 * effect commits the new value before any passive effect can read it.
 *
 * Read `.current` only from effects, event handlers or callbacks — never during render.
 */
export const useLatestRef = <T>(value: T) => {
  const ref = useRef(value);

  useIsomorphicLayoutEffect(() => {
    ref.current = value;
  });

  return ref;
};
